import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useMessages, useMessagesStatus } from "../context/MessagesContext";
import { getSuerosData } from "../lib/suerosData";
import { getTratamientosData } from "../lib/tratamientosData";
import { useLanguage } from "../context/LanguageContext";

const SITE_URL = "https://hidromedmx.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`;
const CLINIC_ID = `${SITE_URL}/#clinic`;
const WEBSITE_ID = `${SITE_URL}/#website`;

const LANGUAGES = [
  { code: "es", hreflang: "es-MX", locale: "es_MX" },
  { code: "en", hreflang: "en-US", locale: "en_US" },
];

function cleanText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function joinText(value) {
  if (Array.isArray(value)) {
    return value.map(cleanText).filter(Boolean).join(". ");
  }

  return cleanText(value);
}

function firstText(...values) {
  return values.map(joinText).find(Boolean) || "";
}

function setMetaTag(attribute, key, content) {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!content) {
    if (tag) tag.remove();
    return;
  }

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function setCanonical(url) {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", url);
}

function setAlternateLinks(path) {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((link) => link.remove());

  LANGUAGES.forEach((language) => {
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", language.hreflang);
    link.setAttribute("href", buildLocalizedUrl(path, language.code));
    link.dataset.seoAlternate = "true";
    document.head.appendChild(link);
  });

  const defaultLink = document.createElement("link");
  defaultLink.setAttribute("rel", "alternate");
  defaultLink.setAttribute("hreflang", "x-default");
  defaultLink.setAttribute("href", buildLocalizedUrl(path, "es"));
  defaultLink.dataset.seoAlternate = "true";
  document.head.appendChild(defaultLink);
}

function setJsonLd(id, data) {
  let script = document.getElementById(id);

  if (!data) {
    if (script) script.remove();
    return;
  }

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

function normalizePath(pathname) {
  const clean = pathname
    .split(/[?#]/)[0]
    .replace(/\/+$/, "")
    .toLowerCase();

  return clean || "/";
}

function trimDescription(value, maxLength = 158) {
  const text = joinText(value).replace(/\s+/g, " ");

  if (!text) return "";
  if (text.length <= maxLength) return text;

  return `${text.slice(0, maxLength - 3).trim()}...`;
}

function titleFromSlug(slug) {
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

function normalizeLanguage(lang) {
  return LANGUAGES.some((language) => language.code === lang)
    ? lang
    : "es";
}

function localeForLanguage(lang) {
  return LANGUAGES.find(
    (language) => language.code === normalizeLanguage(lang)
  );
}

function buildUrl(path) {
  const cleanPath = normalizePath(path);

  return `${SITE_URL}${cleanPath === "/" ? "/" : cleanPath}`;
}

function buildLocalizedUrl(path, lang) {
  const url = new URL(buildUrl(path));

  if (normalizeLanguage(lang) !== "es") {
    url.searchParams.set("lang", normalizeLanguage(lang));
  }

  return url.toString();
}

function absoluteAssetUrl(assetPath) {
  const asset = cleanText(assetPath);

  if (!asset) return DEFAULT_OG_IMAGE;

  return new URL(asset, SITE_URL).toString();
}

function baseBreadcrumb(labels) {
  return [
    {
      name: firstText(labels["nav.home"], "Inicio"),
      path: "/",
    },
  ];
}

function getRouteSeo(path, labels, sueros, tratamientos) {
  const titleFallback =
    labels["seo.default.title"] ||
    "IV Therapy en Tijuana | NAD+ IV y Células Madre | Hidromed MX";

  const descriptionFallback =
    labels["seo.default.description"] ||
    "Clínica en Zona Río, Tijuana, especializada en IV Therapy, NAD+ IV, células madre y tratamientos faciales. Atención personalizada para pacientes de Tijuana, San Diego y California.";

  const sueroMatch = path.match(/^\/sueros\/([^/]+)$/);

  if (sueroMatch) {
    const slug = sueroMatch[1].toLowerCase();
    const suero = sueros.find((item) => item.slug === slug);

    if (suero) {
      const title = firstText(suero.title, titleFromSlug(slug));

      return {
        title: `${title} | ${
          labels["seo.sueros.detail.suffix"] ||
          "IV Therapy en Tijuana | Hidromed MX"
        }`,
        description: trimDescription(
          firstText(
            suero.backInfo,
            suero.suggested,
            suero.description,
            descriptionFallback
          )
        ),
        image: suero.image,
        serviceName: title,
        serviceType:
          labels["seo.sueros.service.type"] || "IV Therapy y terapia de infusión intravenosa",
        schemaType: "MedicalWebPage",
        breadcrumb: [
          ...baseBreadcrumb(labels),
          {
            name: firstText(labels["nav.sueros"], "Sueros"),
            path: "/sueros",
          },
          {
            name: title,
            path,
          },
        ],
      };
    }

    return {
      title: `Suero no encontrado | ${
        labels["seo.siteName"] || "Hidromed MX"
      }`,
      description:
        labels["seo.sueros.description"] || descriptionFallback,
      canonicalPath: "/sueros",
      robots: "noindex, follow",
      breadcrumb: [
        ...baseBreadcrumb(labels),
        {
          name: firstText(labels["nav.sueros"], "Sueros"),
          path: "/sueros",
        },
      ],
    };
  }

  const tratamientoMatch = path.match(
    /^\/tratamientos\/([^/]+)$/
  );

  if (tratamientoMatch) {
    const slug = tratamientoMatch[1].toLowerCase();

    const tratamiento = tratamientos.find(
      (item) => item.slug === slug
    );

    if (tratamiento) {
      const title = firstText(
        tratamiento.title,
        titleFromSlug(slug)
      );

      return {
        title: `${title} | ${
          labels["seo.tratamientos.detail.suffix"] ||
          "Tratamientos en Tijuana | Hidromed MX"
        }`,
        description: trimDescription(
          firstText(
            tratamiento.description,
            descriptionFallback
          )
        ),
        image: tratamiento.image,
        serviceName: title,
        serviceType:
          labels["seo.service.type"] || "Medicina regenerativa, células madre y tratamientos faciales",
        schemaType: "MedicalWebPage",
        breadcrumb: [
          ...baseBreadcrumb(labels),
          {
            name: firstText(
              labels["nav.tratamientos"],
              "Tratamientos"
            ),
            path: "/tratamientos",
          },
          {
            name: title,
            path,
          },
        ],
      };
    }

    return {
      title: `Tratamiento no encontrado | ${
        labels["seo.siteName"] || "Hidromed MX"
      }`,
      description:
        labels["seo.tratamientos.description"] ||
        descriptionFallback,
      canonicalPath: "/tratamientos",
      robots: "noindex, follow",
      breadcrumb: [
        ...baseBreadcrumb(labels),
        {
          name: firstText(
            labels["nav.tratamientos"],
            "Tratamientos"
          ),
          path: "/tratamientos",
        },
      ],
    };
  }

  const byPath = {
    "/": {
      title:
        labels["seo.home.title"] ||
        titleFallback,
      description:
        labels["seo.home.description"] ||
        descriptionFallback,
      canonicalPath: "/",
      schemaType: "WebPage",
      breadcrumb: baseBreadcrumb(labels),
    },

    "/home": {
      title:
        labels["seo.home.title"] ||
        titleFallback,
      description:
        labels["seo.home.description"] ||
        descriptionFallback,
      canonicalPath: "/",
      schemaType: "WebPage",
      breadcrumb: baseBreadcrumb(labels),
    },

    "/sueros": {
      title:
        labels["seo.sueros.title"] ||
        titleFallback,
      description:
        labels["seo.sueros.description"] ||
        descriptionFallback,
      schemaType: "CollectionPage",

      itemList: sueros.map((item) => ({
        name: firstText(
          item.title,
          titleFromSlug(item.slug)
        ),
        path: `/sueros/${item.slug}`,
        image: item.image,
      })),

      breadcrumb: [
        ...baseBreadcrumb(labels),
        {
          name: firstText(
            labels["nav.sueros"],
            "Sueros"
          ),
          path: "/sueros",
        },
      ],
    },

    "/tratamientos": {
      title:
        labels["seo.tratamientos.title"] ||
        titleFallback,
      description:
        labels["seo.tratamientos.description"] ||
        descriptionFallback,
      schemaType: "CollectionPage",

      itemList: tratamientos.map((item) => ({
        name: firstText(
          item.title,
          titleFromSlug(item.slug)
        ),
        path: `/tratamientos/${item.slug}`,
        image: item.image,
      })),

      breadcrumb: [
        ...baseBreadcrumb(labels),
        {
          name: firstText(
            labels["nav.tratamientos"],
            "Tratamientos"
          ),
          path: "/tratamientos",
        },
      ],
    },

    "/empresas": {
      title:
        labels["seo.empresas.title"] ||
        "Nutri C IV para empresas en Tijuana | Hidromed MX",

      description:
        labels["seo.empresas.description"] ||
        "Paquete empresarial Nutri C IV para equipos de 15 personas o más. Servicio onsite en Tijuana con atención profesional y opciones personalizadas.",

      schemaType: "WebPage",
      serviceName: "Nutri C IV para empresas",
      serviceType: "Bienestar corporativo onsite",

      breadcrumb: [
        ...baseBreadcrumb(labels),
        {
          name: firstText(
            labels["nav.empresas"],
            "Empresas"
          ),
          path: "/empresas",
        },
      ],
    },

    "/nosotros": {
      title:
        labels["seo.about.title"] ||
        titleFallback,
      description:
        labels["seo.about.description"] ||
        descriptionFallback,
      schemaType: "AboutPage",

      breadcrumb: [
        ...baseBreadcrumb(labels),
        {
          name: firstText(
            labels["nav.about"],
            "Nosotros"
          ),
          path: "/nosotros",
        },
      ],
    },

    "/contacto": {
      title:
        labels["seo.contact.title"] ||
        titleFallback,
      description:
        labels["seo.contact.description"] ||
        descriptionFallback,
      schemaType: "ContactPage",

      breadcrumb: [
        ...baseBreadcrumb(labels),
        {
          name: firstText(
            labels["nav.contact"],
            "Contacto"
          ),
          path: "/contacto",
        },
      ],
    },

    "/reservar": {
      title:
        labels["seo.reserve.title"] ||
        titleFallback,
      description:
        labels["seo.reserve.description"] ||
        descriptionFallback,
      schemaType: "WebPage",

      breadcrumb: [
        ...baseBreadcrumb(labels),
        {
          name: firstText(
            labels["nav.reserve"],
            "Reservar"
          ),
          path: "/reservar",
        },
      ],
    },
  };

  return (
    byPath[path] || {
      title: titleFallback,
      description: descriptionFallback,
      canonicalPath: "/",
      robots: "noindex, follow",
      breadcrumb: baseBreadcrumb(labels),
    }
  );
}

function createClinicSchema(lang) {
  const isEnglish = normalizeLanguage(lang) === "en";

  return {
    "@type": ["MedicalClinic", "LocalBusiness"],
    "@id": CLINIC_ID,

    name: "Hidromed MX",
    url: `${SITE_URL}/`,
    logo: DEFAULT_OG_IMAGE,
    image: DEFAULT_OG_IMAGE,

    description: isEnglish
      ? "Clinic in Zona Rio, Tijuana, specializing in IV Therapy, NAD+ IV, stem cells, regenerative medicine and facial treatments, serving patients from Tijuana, San Diego and California."
      : "Clínica en Zona Río, Tijuana, especializada en IV Therapy, NAD+ IV, células madre, medicina regenerativa y tratamientos faciales, con atención para pacientes de Tijuana, San Diego y California.",

    telephone: "+52 664 195 4918",
    email: "hidromed.mx@gmail.com",
    priceRange: "$$",

    address: {
      "@type": "PostalAddress",
      streetAddress:
        "Blvd. Padre Kino 10840, Local 1 - 2do Piso, Zona Urbana Río",
      addressLocality: "Tijuana",
      addressRegion: "Baja California",
      postalCode: "22010",
      addressCountry: "MX",
    },

    geo: {
      "@type": "GeoCoordinates",
      latitude: 32.5265873,
      longitude: -117.0092127,
    },

    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
        ],
        opens: "09:00",
        closes: "17:00",
      },

      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "08:00",
        closes: "15:00",
      },

      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Sunday",
        opens: "09:00",
        closes: "15:00",
      },
    ],

    sameAs: [
      "https://www.instagram.com/hidromed.mx/",
      "https://facebook.com/p/Hidromed-Tijuana-100076453744281/",
      "https://tiktok.com/@hidromed.mx",
    ],

    areaServed: [
      {
        "@type": "City",
        name: "Tijuana",
      },
      {
        "@type": "AdministrativeArea",
        name: "Baja California",
      },
      {
        "@type": "AdministrativeArea",
        name: "California",
      },
    ],

    medicalSpecialty: [
      "IV therapy",
      "Regenerative medicine",
      "Aesthetic medicine",
    ],

    knowsAbout: isEnglish
      ? [
          "IV Therapy in Tijuana",
          "IV infusion therapy",
          "NAD+ IV",
          "Vitamin IV therapy",
          "Stem cells in Tijuana",
          "Facial treatments",
          "Regenerative medicine",
        ]
      : [
          "IV Therapy en Tijuana",
          "Terapia de infusión intravenosa",
          "NAD+ IV",
          "Sueros vitaminados",
          "Sueroterapia intravenosa",
          "Células madre en Tijuana",
          "Tratamientos faciales",
          "Medicina regenerativa",
        ],

    hasOfferCatalog: {
      "@type": "OfferCatalog",

      name: isEnglish
        ? "Hidromed MX Services"
        : "Servicios de Hidromed MX",

      itemListElement: [
        {
          "@type": "Offer",

          itemOffered: {
            "@type": "Service",

            name: isEnglish
              ? "IV Therapy in Tijuana"
              : "IV Therapy en Tijuana",

            serviceType: isEnglish
              ? "IV infusion therapy"
              : "Terapia de infusión intravenosa",
          },
        },

        {
          "@type": "Offer",

          itemOffered: {
            "@type": "Service",

            name: isEnglish
              ? "Stem Cells in Tijuana"
              : "Células madre en Tijuana",

            serviceType: isEnglish
              ? "Regenerative medicine"
              : "Medicina regenerativa",
          },
        },

        {
          "@type": "Offer",

          itemOffered: {
            "@type": "Service",

            name: isEnglish
              ? "Facial Treatments in Tijuana"
              : "Tratamientos faciales en Tijuana",

            serviceType: isEnglish
              ? "Aesthetic medicine"
              : "Estética facial",
          },
        },
      ],
    },
  };
}

function createSeoGraph({
  seo,
  canonicalUrl,
  lang,
  siteName,
  image,
}) {
  const language =
    localeForLanguage(lang).hreflang;

  const graph = [
    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      url: `${SITE_URL}/`,
      name: siteName,
      inLanguage: language,
      publisher: {
        "@id": CLINIC_ID,
      },
    },

    createClinicSchema(lang),

    {
      "@type":
        seo.schemaType ||
        "WebPage",

      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: seo.title,
      description: seo.description,
      inLanguage: language,

      isPartOf: {
        "@id": WEBSITE_ID,
      },

      about: {
        "@id": CLINIC_ID,
      },

      primaryImageOfPage: {
        "@type": "ImageObject",
        url: image,
      },
    },
  ];

  if (seo.breadcrumb?.length > 1) {
    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${canonicalUrl}#breadcrumb`,

      itemListElement:
        seo.breadcrumb.map(
          (item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: buildLocalizedUrl(
              item.path,
              lang
            ),
          })
        ),
    });
  }

  if (seo.serviceName) {
    graph.push({
      "@type": "Service",
      "@id": `${canonicalUrl}#service`,

      name: seo.serviceName,
      description: seo.description,
      serviceType: seo.serviceType,
      url: canonicalUrl,
      image,

      provider: {
        "@id": CLINIC_ID,
      },

      areaServed: [
        {
          "@type": "City",
          name: "Tijuana",
        },
        {
          "@type": "AdministrativeArea",
          name: "Baja California",
        },
        {
          "@type": "AdministrativeArea",
          name: "California",
        },
      ],
    });
  }

  if (seo.itemList?.length) {
    graph.push({
      "@type": "ItemList",
      "@id": `${canonicalUrl}#itemlist`,
      name: seo.title,

      itemListElement:
        seo.itemList.map(
          (item, index) => ({
            "@type": "ListItem",
            position: index + 1,

            url: buildLocalizedUrl(
              item.path,
              lang
            ),

            item: {
              "@type": "Service",
              name: item.name,

              url: buildLocalizedUrl(
                item.path,
                lang
              ),

              image:
                absoluteAssetUrl(
                  item.image
                ),

              provider: {
                "@id": CLINIC_ID,
              },
            },
          })
        ),
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

export default function SeoManager() {
  const location = useLocation();
  const labels = useMessages();
  const { lang } = useLanguage();
  const messagesStatus = useMessagesStatus();

  // Solo se generan metadatos con los labels del idioma actual ya cargados;
  // mientras tanto (o si la carga falla) se conservan los del HTML inicial.
  const messagesReady =
    messagesStatus.lang === lang && !messagesStatus.error;

  const sueros = useMemo(
    () => getSuerosData(labels),
    [labels]
  );

  const tratamientos = useMemo(
    () => getTratamientosData(labels),
    [labels]
  );

  useEffect(() => {
    if (!messagesReady) return;

    const path =
      normalizePath(location.pathname);

    const seo =
      getRouteSeo(
        path,
        labels,
        sueros,
        tratamientos
      );

    const siteName =
      labels["seo.siteName"] ||
      "Hidromed MX";

    const language =
      localeForLanguage(lang);

    const canonicalPath =
      seo.canonicalPath ||
      path;

    const canonicalUrl =
      buildLocalizedUrl(
        canonicalPath,
        lang
      );

    const image =
      absoluteAssetUrl(
        seo.image
      );

    const robots =
      seo.robots ||
      "index, follow, max-snippet:-1, max-image-preview:large";

    document.title =
      seo.title;

    document.documentElement.setAttribute(
      "lang",
      normalizeLanguage(lang)
    );

    setCanonical(
      canonicalUrl
    );

    setAlternateLinks(
      canonicalPath
    );

    setMetaTag(
      "name",
      "description",
      seo.description
    );

    setMetaTag(
      "name",
      "robots",
      robots
    );

    setMetaTag(
      "name",
      "keywords",
      cleanText(
        labels[
          "seo.default.keywords"
        ]
      )
    );

    setMetaTag(
      "name",
      "author",
      siteName
    );

    setMetaTag(
      "name",
      "theme-color",
      "#0e7490"
    );

    setMetaTag(
      "property",
      "og:site_name",
      siteName
    );

    setMetaTag(
      "property",
      "og:title",
      seo.title
    );

    setMetaTag(
      "property",
      "og:description",
      seo.description
    );

    setMetaTag(
      "property",
      "og:url",
      canonicalUrl
    );

    setMetaTag(
      "property",
      "og:type",
      "website"
    );

    setMetaTag(
      "property",
      "og:locale",
      language.locale
    );

    setMetaTag(
      "property",
      "og:locale:alternate",
      LANGUAGES.find(
        (item) =>
          item.code !==
          normalizeLanguage(lang)
      )?.locale
    );

    setMetaTag(
      "property",
      "og:image",
      image
    );

    setMetaTag(
      "property",
      "og:image:secure_url",
      image
    );

    setMetaTag(
      "property",
      "og:image:alt",
      `${siteName} - ${seo.title}`
    );

    setMetaTag(
      "name",
      "twitter:card",
      "summary_large_image"
    );

    setMetaTag(
      "name",
      "twitter:title",
      seo.title
    );

    setMetaTag(
      "name",
      "twitter:description",
      seo.description
    );

    setMetaTag(
      "name",
      "twitter:image",
      image
    );

    setJsonLd(
      "seo-jsonld",
      createSeoGraph({
        seo,
        canonicalUrl,
        lang,
        siteName,
        image,
      })
    );
  }, [
    location.pathname,
    labels,
    sueros,
    tratamientos,
    lang,
    messagesReady,
  ]);

  return null;
}